import { useEffect, useState } from "react"
import { Card, CardContent } from "@/components/ui/card"
import { Progress } from "@/components/ui/progress"
import { Sparkles, Wand2 } from "lucide-react"
import { cn } from "@/lib/utils/general"
import { useNavigate } from "react-router-dom"
import { createThemeDeck } from "@/services/api"
import { useToast } from "@/hooks/useToast"

interface ThemeCreationProgressProps {
  themeId: string
  artStyleId: string
}

const PROGRESS_STEPS = [
  { message: "Gathering theme elements...", progress: 25 },
  { message: "Designing card layouts...", progress: 50 },
  { message: "Applying art style...", progress: 75 },
  { message: "Finalizing your deck...", progress: 90 }
]

export function ThemeCreationProgress({ themeId, artStyleId }: ThemeCreationProgressProps) {
  const [progress, setProgress] = useState(0)
  const [currentStep, setCurrentStep] = useState(0)
  const navigate = useNavigate()
  const { toast } = useToast()

  useEffect(() => {
    const createDeck = async () => {
      try {
        const response = await createThemeDeck(themeId, artStyleId)
        if (response.success && response.data.deckId) {
          // Wait for the progress animation to complete
          setTimeout(() => {
            navigate(`/decks/${response.data.deckId}`)
          }, 15000)
        } else {
          throw new Error('Failed to get deck ID from response')
        }
      } catch (error) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to create deck. Please try again."
        })
      }
    }

    createDeck()
  }, [themeId, artStyleId, navigate, toast])

  useEffect(() => {
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        const nextProgress = PROGRESS_STEPS[currentStep]?.progress || 100
        if (prev < nextProgress) {
          return prev + 1
        }
        return prev
      })
    }, 50)

    const stepInterval = setInterval(() => {
      setCurrentStep(prev => {
        if (prev < PROGRESS_STEPS.length) {
          return prev + 1
        }
        return prev
      })
    }, 4000)

    return () => {
      clearInterval(progressInterval)
      clearInterval(stepInterval)
    }
  }, [currentStep])

  return (
    <Card className="w-full max-w-2xl mx-auto animate-slideUp">
      <CardContent className="p-8">
        <div className="flex flex-col items-center space-y-8">
          <div className="relative w-20 h-20">
            <div className="absolute inset-0 flex items-center justify-center">
              <Wand2 className="w-8 h-8 text-game-accent animate-pulse" />
            </div>
            <div className="absolute inset-0">
              <div className="w-full h-full rounded-full border-4 border-game-accent/20 animate-spin-slow" />
            </div>
            <div className="absolute inset-0">
              <div className="w-full h-full rounded-full border-4 border-t-game-accent animate-spin" />
            </div>
          </div>

          <div className="w-full space-y-4">
            <Progress value={progress} className="w-full h-2" />
            <div className="text-center">
              <p className="text-lg font-semibold text-foreground">
                {PROGRESS_STEPS[currentStep]?.message || "Almost there..."}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Please wait while we create your theme deck
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Sparkles className={cn(
              "w-4 h-4 transition-opacity",
              progress === 100 ? "opacity-100" : "opacity-50"
            )} />
            <span>{progress}% Complete</span>
          </div>
        </div>
      </CardContent>
    </Card>
  )
} 